"use strict";
import { getExploreDescription, getExploreTitle } from "pages/getExploreTitle";
import { getAddLiquidityPageTitle, getPositionPageDescription, getPositionPageTitle } from "pages/getPositionPageTitle";
import { Suspense, lazy, useMemo } from "react";
import { Navigate, Route, Routes, matchPath, useLocation } from "react-router-dom";
import { useEnabledChains } from "uniswap/src/features/chains/hooks/useEnabledChains";
import { EXTENSION_PASSKEY_AUTH_PATH } from "uniswap/src/features/passkey/constants";
import { isBrowserRouterEnabled } from "utils/env";
import Mint from "pages/Mint";
import RingWrap from "pages/RingWrap";
import Swap from "pages/Swap";
import { FeatureFlags } from "uniswap/src/features/gating/flags";
import { useFeatureFlag } from "uniswap/src/features/gating/hooks";
import i18n from "uniswap/src/i18n";
const CreatePosition = lazy(() => import("pages/Pool/Positions/create/CreatePosition"));
const AddLiquidityV3WithTokenRedirects = lazy(() => import("pages/AddLiquidityV3/redirects"));
const AddLiquidityV2WithTokenRedirects = lazy(() => import("pages/AddLiquidityV2/redirects"));
const Earn = lazy(() => import("pages/Earn"));
const RedirectExplore = lazy(() => import("pages/Explore/redirects"));
const Markets = lazy(() => import("pages/Markets"));
const MarketDetails = lazy(() => import("pages/MarketDetails"));
const MigrateV2 = lazy(() => import("pages/MigrateV2"));
const MigrateV2Pair = lazy(() => import("pages/MigrateV2/MigrateV2Pair"));
const MigrateV3 = lazy(() => import("pages/MigrateV3"));
const NotFound = lazy(() => import("pages/NotFound"));
const Pool = lazy(() => import("pages/Pool"));
const LegacyPoolRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.LegacyPoolRedirects }))
);
const PoolFinderRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.PoolFinderRedirects }))
);
const LegacyPositionPageRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.LegacyPositionPageRedirects }))
);
const RemoveLiquidityV2WithTokenRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.RemoveLiquidityV2WithTokenRedirects }))
);
const PositionPage = lazy(() => import("pages/Pool/Positions/PositionPage"));
const V2PositionPage = lazy(() => import("pages/Pool/Positions/V2PositionPage"));
const FewV2PositionPage = lazy(() => import("pages/Pool/Positions/FewV2PositionPage"));
const FewTokenLiquidityPage = lazy(() => import("pages/Pool/Positions/FewTokenLiquidityPage"));
const FewTokenPositionsPage = lazy(() => import("pages/Pool/Positions/FewTokenPositionsPage"));
const PoolDetails = lazy(() => import("pages/PoolDetails"));
const TokenDetails = lazy(() => import("pages/TokenDetails"));
const VaultDetails = lazy(() => import("pages/VaultDetails"));
const TermsOfService = lazy(() => import("pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("pages/PrivacyPolicy"));
const ExtensionPasskeyAuthPopUp = lazy(() => import("pages/ExtensionPasskeyAuthPopUp"));
const PasskeyManagement = lazy(() => import("pages/PasskeyManagement"));
const Referral = lazy(() => import("pages/Referral"));
const Advanced = lazy(() => import("pages/Advanced"));
const Susdr = lazy(() => import("pages/Susdr"));
function AdvancedRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/advanced", search }} replace />;
}
function FewTokenRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/positions/create/fewtoken", search }} replace />;
}
export function useRouterConfig() {
  const browserRouterEnabled = isBrowserRouterEnabled();
  const { hash } = useLocation();
  const isEmbeddedWalletEnabled = useFeatureFlag(FeatureFlags.EmbeddedWallet);
  const { isTestnetModeEnabled } = useEnabledChains();
  return useMemo(
    () => ({
      browserRouterEnabled,
      hash,
      isEmbeddedWalletEnabled,
      isTestnetModeEnabled
    }),
    [browserRouterEnabled, hash, isEmbeddedWalletEnabled, isTestnetModeEnabled]
  );
}
const StaticTitlesAndDescriptions = {
  UniswapTitle: i18n.t("title.uniswapTradeCrypto"),
  SwapTitle: i18n.t("title.buySellTradeEthereum"),
  SwapDescription: i18n.t("title.swappingMadeSimple"),
  DetailsPageBaseTitle: i18n.t("common.buyAndSell"),
  TDPDescription: i18n.t("title.realTime"),
  PDPDescription: i18n.t("title.tradeTokens"),
  MigrateTitle: i18n.t("title.migratev2"),
  MigrateTitleV3: i18n.t("title.migratev3"),
  MigrateDescription: i18n.t("title.easilyRemove"),
  MigrateDescriptionV4: i18n.t("title.easilyRemoveV4"),
  AddLiquidityDescription: i18n.t("title.earnFees"),
  PasskeyManagementTitle: i18n.t("title.managePasskeys")
};
function createRouteDefinition(route) {
  return {
    getElement: () => null,
    getTitle: () => StaticTitlesAndDescriptions.UniswapTitle,
    getDescription: () => StaticTitlesAndDescriptions.SwapDescription,
    enabled: () => true,
    path: "/",
    nestedPaths: [],
    // overwrite the defaults
    ...route
  };
}
export const routes = [
  createRouteDefinition({
    path: "/",
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle,
    getDescription: () => StaticTitlesAndDescriptions.SwapDescription,
    getElement: (args) => {
      return args.browserRouterEnabled && args.hash ? <Navigate to={args.hash.replace("#", "")} replace /> : <Navigate to="/swap" replace />;
    }
  }),
  createRouteDefinition({
    path: "/explore",
    getTitle: getExploreTitle,
    getDescription: getExploreDescription,
    nestedPaths: [":tab", ":chainName", ":tab/:chainName"],
    getElement: () => <RedirectExplore />
  }),
  createRouteDefinition({
    path: "/lending",
    getTitle: () => i18n.t("common.lending"),
    getDescription: () => i18n.t("common.browseLendingMarkets"),
    getElement: () => <Markets />
  }),
  createRouteDefinition({
    path: "/vaults",
    getTitle: () => i18n.t("common.vaults"),
    getDescription: () => i18n.t("common.browseLendingVaults"),
    getElement: () => <Markets />
  }),
  createRouteDefinition({
    path: "/lending/:chainName/:marketId",
    getTitle: () => i18n.t("common.lendingMarketDetails"),
    getDescription: () => i18n.t("common.inspectLendingMarket"),
    getElement: () => <MarketDetails />
  }),
  createRouteDefinition({
    path: "/vaults/:chainName/:vaultId",
    getTitle: () => i18n.t("common.lendingVaultDetails"),
    getDescription: () => i18n.t("common.inspectLendingVault"),
    getElement: () => <VaultDetails />
  }),
  createRouteDefinition({
    path: "/explore/tokens/:chainName/:tokenAddress",
    getTitle: () => i18n.t("common.buyAndSell"),
    getDescription: () => StaticTitlesAndDescriptions.TDPDescription,
    getElement: () => <TokenDetails />
  }),
  createRouteDefinition({
    path: "/tokens",
    getTitle: getExploreTitle,
    getDescription: getExploreDescription,
    getElement: () => <Navigate to="/explore/tokens" replace />
  }),
  createRouteDefinition({
    path: "/tokens/:chainName",
    getTitle: getExploreTitle,
    getDescription: getExploreDescription,
    getElement: () => <RedirectExplore />
  }),
  createRouteDefinition({
    path: "/tokens/:chainName/:tokenAddress",
    getTitle: () => StaticTitlesAndDescriptions.DetailsPageBaseTitle,
    getDescription: () => StaticTitlesAndDescriptions.TDPDescription,
    getElement: () => <RedirectExplore />
  }),
  createRouteDefinition({
    path: "/explore/pools/:chainName/:poolAddress",
    getTitle: () => StaticTitlesAndDescriptions.DetailsPageBaseTitle,
    getDescription: () => StaticTitlesAndDescriptions.PDPDescription,
    getElement: () => <Suspense fallback={null}><PoolDetails /></Suspense>
  }),
  createRouteDefinition({
    path: "/vote/*",
    getTitle: () => i18n.t("title.voteOnGov"),
    getDescription: () => i18n.t("title.uniToken"),
    getElement: () => {
      return <Routes><Route
        path="*"
        Component={() => {
          window.location.href = "https://vote.uniswapfoundation.org";
          return null;
        }}
      /></Routes>;
    }
  }),
  createRouteDefinition({
    path: "/create-proposal",
    getTitle: () => i18n.t("title.createGovernanceOn"),
    getDescription: () => i18n.t("title.createGovernanceTo"),
    getElement: () => <Navigate to="/vote/create-proposal" replace />
  }),
  createRouteDefinition({
    path: "/buy",
    getElement: () => <Swap />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  createRouteDefinition({
    path: "/sell",
    getElement: () => <Swap />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  createRouteDefinition({
    path: "/send",
    getElement: () => <Swap />,
    getTitle: () => i18n.t("title.sendTokens")
  }),
  createRouteDefinition({
    path: "/limits",
    getElement: () => <AdvancedRedirect />,
    getTitle: () => i18n.t("title.placeLimit")
  }),
  createRouteDefinition({
    path: "/limit",
    getElement: () => <AdvancedRedirect />,
    getTitle: () => i18n.t("title.placeLimit")
  }),
  createRouteDefinition({
    path: "/limit-order",
    getElement: () => <AdvancedRedirect />,
    getTitle: () => i18n.t("title.placeLimit")
  }),
  createRouteDefinition({
    path: "/advanced",
    getElement: () => <Advanced />,
    getTitle: () => i18n.t("title.placeLimit")
  }),
  createRouteDefinition({
    path: "/twap",
    getElement: () => <Advanced />,
    getTitle: () => i18n.t("title.placeLimit")
  }),
  createRouteDefinition({
    path: "/stop-loss",
    getElement: () => <Advanced />,
    getTitle: () => i18n.t("title.placeLimit")
  }),
  createRouteDefinition({
    path: "/take-profit",
    getElement: () => <Advanced />,
    getTitle: () => i18n.t("title.placeLimit")
  }),
  createRouteDefinition({
    path: "/buy",
    getElement: () => <Swap />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  createRouteDefinition({
    path: "/swap",
    getElement: () => <Swap />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  createRouteDefinition({
    path: "/stock",
    getElement: () => <Swap />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  createRouteDefinition({
    path: "/wrap",
    getElement: () => <RingWrap />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  createRouteDefinition({
    path: "/earn",
    getElement: () => <Earn />,
    getTitle: () => "Earn",
    getDescription: () => "Deposit USDR into Earn for fixed yield."
  }),
  createRouteDefinition({
    path: "/mint",
    getElement: () => <Mint />,
    getTitle: () => "Mint USDR",
    getDescription: () => "Deposit supported stablecoins to mint USDR."
  }),
  createRouteDefinition({
    path: "/susdr",
    getElement: () => <Susdr />,
    getTitle: () => "sUSDR",
    getDescription: () => "Monitor sUSDR liquidity and redemption capacity."
  }),
  // Refreshed pool routes
  createRouteDefinition({
    path: "/fewtoken",
    getElement: () => <FewTokenRedirect />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/create",
    getElement: () => <CreatePosition />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription,
    nestedPaths: [":protocolVersion"]
  }),
  createRouteDefinition({
    path: "/positions/maintain/fewtoken",
    getElement: () => <CreatePosition entry="fewtoken-maintenance" />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions",
    getElement: () => <Pool />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/fewtoken",
    getElement: () => <FewTokenPositionsPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/fewtoken/:chainName/:pairAddress",
    getElement: () => <FewV2PositionPage fewTokenManagement />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/fewv2/:chainName/:pairAddress",
    getElement: () => <FewV2PositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/increase/fewtoken/:chainName/:pairAddress",
    getElement: () => <FewTokenLiquidityPage action="increase" />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/remove/fewtoken/:chainName/:pairAddress",
    getElement: () => <FewTokenLiquidityPage action="remove" />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/v2/:chainName/:pairAddress",
    getElement: () => <V2PositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/v3/:chainName/:tokenId",
    getElement: () => <PositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/v4/:chainName/:tokenId",
    getElement: () => <PositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/migrate/v3/:chainName/:tokenId",
    getElement: () => <MigrateV3 />,
    getTitle: () => StaticTitlesAndDescriptions.MigrateTitleV3,
    getDescription: () => StaticTitlesAndDescriptions.MigrateDescriptionV4
  }),
  // Legacy pool routes
  createRouteDefinition({
    path: "/pool",
    getElement: () => <LegacyPoolRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pool/v2/find",
    getElement: () => <PoolFinderRedirects />,
    getTitle: getPositionPageDescription,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pool/v2",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pool/fewV2/find",
    getElement: () => <PoolFinderRedirects />,
    getTitle: getPositionPageDescription,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pool/fewv2",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pool/:tokenId",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pools/v2/find",
    getElement: () => <PoolFinderRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pools/fewV2/find",
    getElement: () => <PoolFinderRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pools",
    getElement: () => <LegacyPoolRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pools/:tokenId",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/add/v2",
    nestedPaths: [":currencyIdA", ":currencyIdA/:currencyIdB"],
    getElement: () => <AddLiquidityV2WithTokenRedirects />,
    getTitle: getAddLiquidityPageTitle,
    getDescription: () => StaticTitlesAndDescriptions.AddLiquidityDescription
  }),
  createRouteDefinition({
    path: "/add/fewv2",
    nestedPaths: [":currencyIdA", ":currencyIdA/:currencyIdB"],
    getElement: () => <AddLiquidityV2WithTokenRedirects />,
    getTitle: getAddLiquidityPageTitle,
    getDescription: () => StaticTitlesAndDescriptions.AddLiquidityDescription
  }),
  createRouteDefinition({
    path: "/add",
    nestedPaths: [
      ":currencyIdA",
      ":currencyIdA/:currencyIdB",
      ":currencyIdA/:currencyIdB/:feeAmount",
      ":currencyIdA/:currencyIdB/:feeAmount/:tokenId"
    ],
    getElement: () => <AddLiquidityV3WithTokenRedirects />,
    getTitle: getAddLiquidityPageTitle,
    getDescription: () => StaticTitlesAndDescriptions.AddLiquidityDescription
  }),
  createRouteDefinition({
    path: "/remove/v2/:currencyIdA/:currencyIdB",
    getElement: () => <RemoveLiquidityV2WithTokenRedirects />,
    getTitle: () => i18n.t("title.removeLiquidityv2"),
    getDescription: () => i18n.t("title.removeTokensv2")
  }),
  createRouteDefinition({
    path: "/remove/fewv2/:currencyIdA/:currencyIdB",
    getElement: () => <RemoveLiquidityV2WithTokenRedirects />,
    getTitle: () => i18n.t("title.removeLiquidityv2"),
    getDescription: () => i18n.t("title.removeTokensv2")
  }),
  createRouteDefinition({
    path: "/remove/:tokenId",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: () => i18n.t("title.removePoolLiquidity"),
    getDescription: () => i18n.t("title.removev3Liquidity")
  }),
  createRouteDefinition({
    path: "/migrate/v2",
    getElement: () => <MigrateV2 />,
    getTitle: () => StaticTitlesAndDescriptions.MigrateTitle,
    getDescription: () => StaticTitlesAndDescriptions.MigrateDescription
  }),
  createRouteDefinition({
    path: "/migrate/v2/:address",
    getElement: () => <MigrateV2Pair />,
    getTitle: () => StaticTitlesAndDescriptions.MigrateTitle,
    getDescription: () => StaticTitlesAndDescriptions.MigrateDescription
  }),
  createRouteDefinition({
    path: "/migrate/fewv2",
    getElement: () => <MigrateV2 />,
    getTitle: () => StaticTitlesAndDescriptions.MigrateTitle,
    getDescription: () => StaticTitlesAndDescriptions.MigrateDescription
  }),
  createRouteDefinition({
    path: "/migrate/fewv2/:address",
    getElement: () => <MigrateV2Pair />,
    getTitle: () => StaticTitlesAndDescriptions.MigrateTitle,
    getDescription: () => StaticTitlesAndDescriptions.MigrateDescription
  }),
  createRouteDefinition({
    path: EXTENSION_PASSKEY_AUTH_PATH,
    getElement: () => <ExtensionPasskeyAuthPopUp />,
    getTitle: () => i18n.t("title.extensionPasskeyLogIn")
  }),
  createRouteDefinition({
    path: "/manage/passkey/:walletAddress",
    getElement: () => <PasskeyManagement />,
    getTitle: () => StaticTitlesAndDescriptions.PasskeyManagementTitle,
    enabled: (args) => args.isEmbeddedWalletEnabled ?? false
  }),
  createRouteDefinition({
    path: "/referral",
    getElement: () => <Referral />,
    getTitle: () => "Referral"
  }),
  createRouteDefinition({
    path: "/terms-of-service",
    getElement: () => <TermsOfService />,
    getTitle: () => "Ring Labs Terms of Service",
    getDescription: () => "Ring Labs Terms of Service"
  }),
  createRouteDefinition({
    path: "/privacy-policy",
    getElement: () => <PrivacyPolicy />,
    getTitle: () => "Ring Labs Privacy Policy",
    getDescription: () => "Ring Labs Privacy Policy"
  }),
  createRouteDefinition({ path: "*", getElement: () => <Navigate to="/not-found" replace /> }),
  createRouteDefinition({ path: "/not-found", getElement: () => <NotFound /> })
];
export const findRouteByPath = (pathname) => {
  for (const route of routes) {
    const match = matchPath(route.path, pathname);
    if (match) {
      return route;
    }
    const subPaths = route.nestedPaths.map((nestedPath) => `${route.path}/${nestedPath}`);
    for (const subPath of subPaths) {
      const match2 = matchPath(subPath, pathname);
      if (match2) {
        return route;
      }
    }
  }
  return void 0;
};
